"use client";

import { useState, useRef, useEffect } from "react";

/**
 * Observes an element for viewport proximity.
 *
 * @param rootMargin  IntersectionObserver rootMargin (e.g. "1200px").
 * @param keepMounted When `true` (default), content stays mounted forever
 *                    after first visibility — good for sections / wrappers.
 *                    When `false`, content unmounts when leaving the
 *                    rootMargin zone — good for product cards in long lists
 *                    so the DOM stays small on mobile.
 */
export function useLazyVisible(
  rootMargin?: string,
  keepMounted = true,
  initialVisible = false,
) {
  const [isVisible, setIsVisible] = useState(initialVisible);
  const ref = useRef<HTMLDivElement>(null);
  const mountedOnceRef = useRef(initialVisible);


  // ── Async IntersectionObserver ───────────────────────────────────
  useEffect(() => {
    // keepMounted mode: once visible, no more work needed
    if (keepMounted && mountedOnceRef.current) return;

    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (keepMounted) {
          // One-way: mount once, stay forever
          if (entry.isIntersecting) {
            mountedOnceRef.current = true;
            setIsVisible(true);
            observer.unobserve(el);
          }
        } else {
          // Two-way: mount when near viewport, unmount when far
          setIsVisible(entry.isIntersecting);
        }
      },
      { threshold: 0, rootMargin },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [rootMargin, keepMounted]);

  return { ref, isVisible };
}
